'use client';

import React from 'react';
import Link from 'next/link';
import { X, Check, Minus, GitCompare } from 'lucide-react';
import type { HealthPackage } from './packagesData';

export default function CompareDrawer({
  packages,
  onRemove,
  onOpen,
  onClose,
  isOpen,
}: {
  packages: HealthPackage[];
  onRemove: (id: string) => void;
  onOpen: () => void;
  onClose: () => void;
  isOpen: boolean;
}) {
  if (!isOpen) {
    return (
      <div className="compare-drawer">
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <GitCompare size={18} className="text-primary" />
            <span className="font-semibold text-sm text-foreground">
              Comparing {packages.length} package{packages.length > 1 ? 's' : ''}
            </span>
            <div className="flex items-center gap-2">
              {packages.map((pkg) => (
                <div key={`compare-pill-${pkg.id}`} className="flex items-center gap-1.5 bg-secondary rounded-full px-3 py-1 text-xs font-medium text-primary">
                  {pkg.name}
                  <button onClick={() => onRemove(pkg.id)} className="hover:text-danger transition-colors" aria-label={`Remove ${pkg.name} from compare`}>
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onOpen} className="btn-primary text-sm py-2 px-4">
              Compare Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Full compare modal
  const allIncludes = Array.from(new Set(packages.flatMap((p) => p.includes)));

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/50 backdrop-blur-sm">
      <div className="bg-card w-full max-w-5xl rounded-t-2xl shadow-modal max-h-[85vh] flex flex-col animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <GitCompare size={18} className="text-primary" />
            <h3 className="font-bold text-lg text-foreground">Package Comparison</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="Close comparison"
          >
            <X size={18} />
          </button>
        </div>

        {/* Compare table */}
        <div className="overflow-auto flex-1 p-6">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left py-3 pr-4 w-48 text-muted-foreground font-medium text-xs uppercase tracking-wider">Parameter</th>
                {packages.map((pkg) => (
                  <th key={`compare-head-${pkg.id}`} className="text-center py-3 px-4 min-w-[180px]">
                    <div className="flex flex-col items-center gap-1">
                      <span className="font-bold text-foreground text-base">{pkg.name}</span>
                      <span className="text-primary font-extrabold stats-number text-xl">
                        ₹{pkg.price.toLocaleString('en-IN')}
                      </span>
                      <button
                        onClick={() => onRemove(pkg.id)}
                        className="text-xs text-muted-foreground hover:text-danger transition-colors flex items-center gap-1"
                      >
                        <X size={10} /> Remove
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Key metrics */}
              {[
                { label: 'Tests Included', key: 'tests' as keyof HealthPackage },
                { label: 'Turnaround', key: 'turnaround' as keyof HealthPackage },
                { label: 'Sample Type', key: 'sampleType' as keyof HealthPackage },
                { label: 'Discount', key: 'discount' as keyof HealthPackage },
              ].map((row) => (
                <tr key={`compare-row-${row.key}`} className="border-t border-border">
                  <td className="py-3 pr-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {row.label}
                  </td>
                  {packages.map((pkg) => (
                    <td key={`compare-cell-${pkg.id}-${row.key}`} className="py-3 px-4 text-center text-sm font-medium text-foreground">
                      {row.key === 'discount' ? `${pkg[row.key]}% OFF` : String(pkg[row.key])}
                    </td>
                  ))}
                </tr>
              ))}

              {/* Fasting */}
              <tr className="border-t border-border bg-muted/20">
                <td className="py-3 pr-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Fasting Required</td>
                {packages.map((pkg) => (
                  <td key={`compare-fasting-${pkg.id}`} className="py-3 px-4 text-center">
                    {pkg.fasting ? (
                      <span className="text-amber-600 text-xs font-semibold">Yes (8–10 hrs)</span>
                    ) : (
                      <span className="text-green-600 text-xs font-semibold">Not Required</span>
                    )}
                  </td>
                ))}
              </tr>

              {/* Tests included */}
              <tr className="border-t-2 border-border">
                <td colSpan={packages.length + 1} className="py-3 text-xs font-bold text-foreground uppercase tracking-wider">
                  Tests & Parameters
                </td>
              </tr>
              {allIncludes.map((inc) => (
                <tr key={`compare-inc-${inc}`} className="border-t border-border/50 hover:bg-muted/30 transition-colors">
                  <td className="py-2.5 pr-4 text-xs text-foreground">{inc}</td>
                  {packages.map((pkg) => (
                    <td key={`compare-inc-cell-${pkg.id}-${inc}`} className="py-2.5 px-4 text-center">
                      {pkg.includes.includes(inc) ? (
                        <Check size={15} className="text-green-600 mx-auto" />
                      ) : (
                        <Minus size={15} className="text-muted-foreground mx-auto opacity-30" />
                      )}
                    </td>
                  ))}
                </tr>
              ))}

              {/* Book row */}
              <tr className="border-t-2 border-border">
                <td className="py-4 pr-4" />
                {packages.map((pkg) => (
                  <td key={`compare-book-${pkg.id}`} className="py-4 px-4 text-center">
                    <Link
                      href={`/book-appointment?package=${pkg.id}`}
                      className="btn-primary text-sm py-2.5 px-4 w-full justify-center"
                    >
                      Book ₹{pkg.price.toLocaleString('en-IN')}
                    </Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}